import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaAngleLeft } from "react-icons/fa";
import { HiOutlineBuildingLibrary } from "react-icons/hi2";
import { FiDownload, FiEye } from "react-icons/fi";
import { jsPDF } from "jspdf";
import axios from "../../../axios";
import { getInstituteName, useInstitute } from "../../../hooks/use-institute";

const formatDate = (date) => {
  if (!date) return "-";

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "-";

  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const Organisation = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await axios.get("me");
        setUser(data?.result || null);
      } catch (error) {
        console.error("Failed to load organisation details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const instituteId =
    (typeof user?.instituteId === "string" && user.instituteId.trim()) ||
    (typeof user?.libraryId === "string" && user.libraryId.trim()) ||
    "";
  const { data: institute } = useInstitute(instituteId);
  const student = user?.student || {};
  const fees = student?.fees || {};
  const seat = student?.seat || {};
  const instituteName =
    getInstituteName(institute) || user?.instituteName || "Organisation";
  const totalFees = fees.totalFees ?? student.totalFees ?? 0;
  const paidFees = fees.paidFees ?? student.paidFees ?? 0;
  const remainingFees = fees.remainingFees ?? student.remainingFees ?? 0;
  const feesPaidOn = fees.feesPaidOn || student.feesPaidOn || "";
  const feePayments = Array.isArray(fees.feePayments)
    ? fees.feePayments
    : Array.isArray(student.feePayments)
      ? student.feePayments
      : [];
  const isLibrary =
    String(institute?.role || student?.role || "").toLowerCase() === "library" ||
    Boolean(student?.seatNumber || seat?.number);

  const detailRows = [
    ["Institute ID", instituteId || "-"],
    ["Name", instituteId ? instituteName : "-"],
    ["Total Fees", `Rs ${totalFees}`],
    ["Paid Fees", `Rs ${paidFees}`],
    ["Remaining Fees", `Rs ${remainingFees}`],
    ["Last Paid On", formatDate(feesPaidOn)],
    ["Enrollment", student?.enrollmentStatus || "-"],
  ];

  const createInvoicePdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 42;
    const invoiceNo = `INV-${student?.studentId || user?.id || "ORG"}`;
    const invoiceDate = new Date().toLocaleDateString("en-IN");

    doc.setFillColor(75, 29, 103);
    doc.rect(0, 0, pageWidth, 96, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.text("Fees Invoice", margin, 52);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(invoiceNo, margin, 74);

    doc.setTextColor(25, 20, 34);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(instituteName || "Organisation", margin, 132);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(`Institute ID: ${instituteId || "-"}`, margin, 152);
    doc.text(`Invoice Date: ${invoiceDate}`, margin, 170);

    doc.setFont("helvetica", "bold");
    doc.text("Student Details", margin, 214);
    doc.setFont("helvetica", "normal");
    doc.text(`Name: ${user?.name || "-"}`, margin, 236);
    doc.text(`Mobile: ${user?.mobile || user?.phoneNumber || "-"}`, margin, 254);
    doc.text(`Email: ${user?.email || "-"}`, margin, 272);
    doc.text(`Enrollment: ${student?.enrollmentStatus || "-"}`, margin, 290);
    doc.text(`Batch: ${student?.batch || "-"}`, margin, 308);

    if (isLibrary) {
      doc.text(`Seat: ${seat?.number || student?.seatNumber || "-"}`, 330, 236);
      doc.text(`Shift: ${seat?.shift || student?.seatShift || "-"}`, 330, 254);
      doc.text(`Seat Status: ${seat?.status || student?.seatStatus || "-"}`, 330, 272);
    }

    const tableTop = 340;
    doc.setFillColor(247, 244, 251);
    doc.rect(margin, tableTop, pageWidth - margin * 2, 42, "F");
    doc.setFont("helvetica", "bold");
    doc.text("Description", margin + 16, tableTop + 26);
    doc.text("Amount", pageWidth - margin - 92, tableTop + 26);

    const rows = [
      ["Total Fees", totalFees],
      ["Paid Fees", paidFees],
      ["Remaining Fees", remainingFees],
    ];

    doc.setFont("helvetica", "normal");
    rows.forEach(([label, amount], index) => {
      const y = tableTop + 72 + index * 34;
      doc.text(label, margin + 16, y);
      doc.text(`Rs ${amount}`, pageWidth - margin - 92, y);
    });

    doc.setDrawColor(225, 219, 232);
    doc.line(margin, tableTop + 182, pageWidth - margin, tableTop + 182);
    doc.setFont("helvetica", "bold");
    doc.text("Balance Due", margin + 16, tableTop + 214);
    doc.text(`Rs ${remainingFees}`, pageWidth - margin - 92, tableTop + 214);

    const paymentTop = tableTop + 260;
    doc.setFillColor(247, 244, 251);
    doc.rect(margin, paymentTop, pageWidth - margin * 2, 36, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(25, 20, 34);
    doc.text("Payment History", margin + 16, paymentTop + 23);

    doc.setFontSize(10);
    doc.text("Date", margin + 16, paymentTop + 58);
    doc.text("Paid Amount", margin + 150, paymentTop + 58);
    doc.text("Total Paid", margin + 280, paymentTop + 58);
    doc.text("Remaining", margin + 400, paymentTop + 58);

    doc.setFont("helvetica", "normal");
    if (feePayments.length > 0) {
      feePayments.forEach((payment, index) => {
        const y = paymentTop + 84 + index * 28;
        doc.text(formatDate(payment.paidOn), margin + 16, y);
        doc.text(`Rs ${payment.amount ?? 0}`, margin + 150, y);
        doc.text(`Rs ${payment.totalPaidFees ?? "-"}`, margin + 280, y);
        doc.text(`Rs ${payment.remainingFees ?? "-"}`, margin + 400, y);
      });
    } else {
      doc.text("No fee payment history available.", margin + 16, paymentTop + 84);
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(107, 96, 115);
    doc.text("This is a system generated invoice.", margin, 760);

    return doc;
  };

  const handleViewInvoice = () => {
    const doc = createInvoicePdf();
    const blobUrl = doc.output("bloburl");
    window.open(blobUrl, "_blank");
  };

  const handleDownloadInvoice = () => {
    const doc = createInvoicePdf();
    doc.save(`fees-invoice-${instituteId || "organisation"}.pdf`);
  };

  return (
    <div className="min-h-screen bg-[#F7F4FB] px-5 py-1 pb-24 text-slate-900 sm:px-6 lg:py-12">
      <div className="mx-auto max-w-2xl">
        <div className="mb-4 mt-4 flex items-center gap-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/60"
          >
            <FaAngleLeft className="h-7 w-7 text-black" />
          </button>
          <h1 className="text-2xl font-bold text-black">Organisation</h1>
        </div>

        {loading ? (
          <div className="flex min-h-[50vh] items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#4B1D67] border-t-transparent" />
          </div>
        ) : (
          <>
            <section className="rounded-[26px] bg-white px-6 py-5 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#F1E8F9]">
                  <HiOutlineBuildingLibrary className="h-7 w-7 text-[#5B2A6D]" />
                </div>
                <div className="min-w-0 text-left">
                  <h2 className="truncate text-lg font-bold text-[#191422]">
                    {instituteId ? instituteName : "No organisation linked"}
                  </h2>
                  <p className="break-all text-sm font-medium text-[#6F6879]">
                    {instituteId || "Institute ID not available"}
                  </p>
                </div>
              </div>

              {instituteId && (
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={handleViewInvoice}
                    className="flex items-center justify-center gap-2 rounded-2xl border border-[#4B1D67]/20 bg-[#F7F4FB] px-3 py-3 text-sm font-bold text-[#4B1D67]"
                  >
                    <FiEye className="h-4 w-4" />
                    View Invoice
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadInvoice}
                    className="flex items-center justify-center gap-2 rounded-2xl bg-[#4B1D67] px-3 py-3 text-sm font-bold text-white"
                  >
                    <FiDownload className="h-4 w-4" />
                    Download PDF
                  </button>
                </div>
              )}
            </section>

            <section className="mt-4 rounded-[26px] bg-white px-5 py-5 shadow-sm">
              <div className="grid gap-3 sm:grid-cols-2">
                {detailRows.map(([label, value]) => (
                  <div key={label} className="rounded-2xl bg-[#F7F4FB] px-4 py-3">
                    <p className="text-xs font-semibold text-[#6B6073]">
                      {label}
                    </p>
                    <p className="mt-1 break-words text-sm font-bold text-[#251936]">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {isLibrary && (
              <section className="mt-4 rounded-[26px] bg-[#FFF4DC] px-5 py-5 shadow-sm">
                <h2 className="text-lg font-bold text-[#251936]">Library Seat</h2>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-xs text-[#6B6073]">Number</p>
                    <p className="font-bold text-[#251936]">
                      {seat?.number || student?.seatNumber || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#6B6073]">Shift</p>
                    <p className="font-bold text-[#251936]">
                      {seat?.shift || student?.seatShift || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#6B6073]">Status</p>
                    <p className="font-bold capitalize text-[#251936]">
                      {seat?.status || student?.seatStatus || "-"}
                    </p>
                  </div>
                </div>
              </section>
            )}

            <section className="mt-4 rounded-[26px] bg-white px-5 py-5 shadow-sm">
              <h2 className="text-lg font-bold text-[#251936]">
                Fee Payment History
              </h2>

              {feePayments.length > 0 ? (
                <div className="mt-4 space-y-3">
                  {feePayments.map((payment, index) => (
                    <div
                      key={payment?._id || index}
                      className="rounded-2xl bg-[#F7F4FB] px-4 py-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-left">
                          <p className="text-sm font-bold text-[#251936]">
                            Payment {index + 1}
                          </p>
                          <p className="mt-1 text-xs font-medium text-[#6B6073]">
                            {formatDate(payment?.paidOn)}
                          </p>
                        </div>
                        <p className="text-sm font-bold text-[#4B1D67]">
                          Rs {payment?.amount ?? 0}
                        </p>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-xs text-[#6B6073]">Total Paid</p>
                          <p className="font-bold text-[#251936]">
                            Rs {payment?.totalPaidFees ?? "-"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-[#6B6073]">Remaining</p>
                          <p className="font-bold text-[#251936]">
                            Rs {payment?.remainingFees ?? "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-sm font-medium text-[#6B6073]">
                  No fee payment history available.
                </p>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default Organisation;
